import React from 'react';
import { Spinner } from 'react-bootstrap';
import { PatternFormat } from 'react-number-format';
import { Form, FormGroup, ValidationMessage } from '~/components/Form';
import { enumValue } from '~/util';
import { ServicesContext } from '~/services';
import { DeletePhone } from './DeletePhone';
import { EnterVerification } from './EnterVerification';

export interface EditPhoneProps {
    edit: () => void;
    userPhone: undefined | UserPhoneType;
    onPhoneUpdate?: (x: UserPhoneType) => void;
    onPhoneDelete?: (x: number) => void;
}
function EditPhone({ edit, userPhone, onPhoneUpdate, onPhoneDelete }: EditPhoneProps) {
    const services = React.useContext(ServicesContext);
    const [phone, setPhone] = React.useState(userPhone?.phone?.number || '');
    const originalPhone = React.useRef(userPhone?.phone?.number || '');
    const [deleting, setDeleting] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [responseMessage, setResponseMessage] = React.useState(false);
    const [validation, setValidation] = React.useState(false);
    const [showVerification, setShowVerification] = React.useState(false);

    async function handleSubmit() {
        try {
            setSaving(true);
            const result = await services.user.setPhone({
                ...(userPhone?.id && { id: userPhone.id }),
                send_verification_code: true,
                phone: {
                    type: enumValue(['phone', 'type', 'Mobile']) as number,
                    number: phone,
                },
            });
            onPhoneUpdate?.(result);
            setShowVerification(true);
        } catch (e) {
            setValidation(true);
            setResponseMessage(e.response);
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            <Form onSubmit={handleSubmit} values={{ phone }} autoComplete="off">
                <FormGroup htmlFor="phone" className="mb-3 row">
                    <label htmlFor="phone" className="col-3 text-end col-form-label">
                        Mobile Number
                    </label>
                    <div className="col-9">
                        <PatternFormat
                            allowEmptyFormatting
                            className="form-control"
                            data-testid="phone"
                            disabled={deleting || showVerification}
                            format="(###) ###-####"
                            id="phone"
                            mask="_"
                            onValueChange={(value) => setPhone(value.value)}
                            value={phone}
                            valueIsNumericString
                        />
                    </div>
                    <ValidationMessage
                        className="col-12 text-center mt-2"
                        rule={validation}
                        type="external"
                        showBeforeSubmit
                    >
                        {Array.isArray(responseMessage)
                            ? responseMessage.map((x) => <div key={x.message}>{x.message}</div>)
                            : responseMessage}
                    </ValidationMessage>
                </FormGroup>
                {!deleting && !showVerification && (
                    <div className="row">
                        <div className="col-4">
                            {userPhone?.id && (
                                <button
                                    className="btn btn-danger me-2"
                                    disabled={deleting}
                                    onClick={() => setDeleting(true)}
                                    type="button"
                                >
                                    Delete Phone
                                </button>
                            )}
                        </div>
                        <div className="col-8 text-end">
                            <button
                                className="btn btn-secondary me-2"
                                disabled={saving}
                                onClick={edit}
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                disabled={
                                    saving ||
                                    phone.length !== 10 ||
                                    (userPhone?.two_factor_verified &&
                                        phone === originalPhone.current)
                                }
                                type="submit"
                            >
                                {saving && (
                                    <Spinner
                                        animation="border"
                                        className="me-1"
                                        role="status"
                                        size="sm"
                                        variant="light"
                                    />
                                )}
                                Verify Phone
                            </button>
                        </div>
                    </div>
                )}
                {deleting && userPhone && (
                    <DeletePhone
                        cancel={() => setDeleting(false)}
                        edit={edit}
                        phoneId={userPhone.id}
                        onPhoneDelete={onPhoneDelete}
                    />
                )}
            </Form>

            {showVerification && userPhone && (
                <EnterVerification edit={edit} userPhone={userPhone} />
            )}
        </>
    );
}

export { EditPhone };
